import { Link } from '@tanstack/react-router';
import { Database } from 'lucide-react';
import { useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useActiveSprints } from '@/hooks/use-sprints';
import { useBoardsTaskStats } from '@/hooks/use-tasks';
import { formatDate, formatDateTime, daysUntilDateOnly } from '@/lib/date';
import { cn } from '@/lib/utils';
import type { BoardListItem, SprintItem } from '@hygge/shared';
import { byRiskThenEndDate, sprintPace, type SprintPace } from '../lib/sprint-pace';

/**
 * The top of the Boards page: what the workspace is doing, said in a sentence,
 * then proved.
 *
 * The old page opened with a count of boards and a count of tasks, which is a
 * directory's opening line, not a summary's. Neither number leads anywhere — a
 * reader who learns there are 1,064 tasks has learned nothing they can act on.
 * This opens with the one fact worth interrupting someone for: whether the work
 * in flight is going to land. Everything below it exists to prove that sentence
 * rather than to decorate it.
 *
 * There are no cards here on purpose. A summary is a sorted list with
 * instrumentation; a grid of panels is what a page reaches for when it has
 * nothing to rank.
 */

const nf = new Intl.NumberFormat('en-US');

/** The house separator, so nine of them are identical. */
function Dot() {
  return <span className="text-muted-foreground/40">·</span>;
}

interface Row {
  board: BoardListItem;
  sprint: SprintItem;
  pace: SprintPace;
}

export function WorkspaceSummary({ boards }: { boards: BoardListItem[] }) {
  const boardIds = useMemo(() => boards.map((b) => b.id), [boards]);
  const sprintQueries = useActiveSprints(boardIds);
  const statsQueries = useBoardsTaskStats(boardIds);

  const sprintsSettled = sprintQueries.every((q) => !q.isPending);

  // Not memoised. `useQueries` hands back a fresh array every render, so a memo
  // over it would either never hit or need a hand-rolled signature to compare —
  // and the work is a map and a sort over one row per board. Paying for the
  // cache would cost more than the computation and add a way to go stale.
  const rows: Row[] = [];
  sprintQueries.forEach((q, i) => {
    const sprint = q.data;
    const board = boards[i];
    if (sprint && board) rows.push({ board, sprint, pace: sprintPace(sprint) });
  });
  rows.sort((a, b) =>
    byRiskThenEndDate(
      { pace: a.pace, endDate: a.sprint.endDate, boardName: a.board.name },
      { pace: b.pace, endDate: b.sprint.endDate, boardName: b.board.name },
    ),
  );

  const atRisk = rows.filter((r) => r.pace.isAtRisk).length;
  const unestimated = rows.filter((r) => r.pace.donePct === null).length;
  const sprintsFailed = sprintQueries.filter((q) => q.isError).length;

  /**
   * A sum is only shown once every board has answered. A total over six of nine
   * boards is not a smaller number, it is a wrong one — and it would settle into
   * the right one a second later, which is worse than showing nothing.
   */
  const statsReady = statsQueries.length > 0 && statsQueries.every((q) => q.isSuccess);
  const statsFailed = statsQueries.filter((q) => q.isError).length;
  const totals = statsReady
    ? statsQueries.reduce(
        (acc, q) => ({
          issues: acc.issues + (q.data?.total ?? 0),
          inProgress: acc.inProgress + (q.data?.byCategory.active ?? 0),
        }),
        { issues: 0, inProgress: 0 },
      )
    : null;

  return (
    <TooltipProvider delayDuration={150}>
      <header className="flex items-baseline justify-between gap-4">
        <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          {formatDateTime(new Date(), { weekday: 'long', day: 'numeric', month: 'long' })}
        </span>
      </header>

      {/* The lede. Connective prose recedes; the figures a decision hangs on are
          the only emphasised words, and each is an anchor to the rows proving it. */}
      <h1 className="mt-6 max-w-[46ch] text-[24px] font-normal leading-[1.3] tracking-[-0.015em] text-muted-foreground">
        {!sprintsSettled ? (
          <Skeleton className="h-7 w-[30ch]" />
        ) : rows.length === 0 ? (
          <span>No sprint is running on any board.</span>
        ) : atRisk === 0 ? (
          <span>
            All <Anchor href="#in-flight">{rows.length} sprints</Anchor> are on pace.
          </span>
        ) : (
          <span>
            <Anchor href="#in-flight">
              {atRisk} of {rows.length} sprints
            </Anchor>{' '}
            {atRisk === 1 ? 'is' : 'are'} <PaceRule boards={rows.length} />.
          </span>
        )}
      </h1>

      {/* The standfirst: the scale of the system, with every figure one hover
          from the query that produced it. */}
      <p className="mt-3 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
        <Figure tip="Boards you can see">{nf.format(boards.length)} boards</Figure>
        <Dot />
        {totals ? (
          <Figure tip="Every task on every board you can see, counted per board by the server">
            {nf.format(totals.issues)} issues
          </Figure>
        ) : (
          <Pending failed={statsFailed} total={statsQueries.length} label="issues" />
        )}
        <Dot />
        {totals ? (
          <Figure tip="Tasks whose status category is Active, counted per board by the server">
            {nf.format(totals.inProgress)} in progress
          </Figure>
        ) : (
          <Pending failed={statsFailed} total={statsQueries.length} label="in progress" />
        )}
        <Dot />
        <Figure tip="Boards with a sprint in status = active">
          {rows.length} sprint{rows.length === 1 ? '' : 's'} running
        </Figure>
      </p>

      <div className="mt-5 border-t border-border" />

      <section id="in-flight" className="mt-7 scroll-mt-6">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="text-[13px] font-semibold text-foreground">In flight</h2>
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {rows.length} running
            {atRisk > 0 ? ` · ${atRisk} behind pace · sorted by risk, then end date` : rows.length > 0 ? ' · all on pace · sorted by end date' : ''}
          </span>
        </div>

        <div className="mt-3 divide-y divide-border/50">
          {!sprintsSettled ? (
            <div className="space-y-px">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-11 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="flex min-h-11 items-center text-xs text-muted-foreground">
              No sprint is running on any board.
            </p>
          ) : (
            rows.map((row) => <SprintRow key={row.sprint.id} {...row} />)
          )}
        </div>

        <p className="mt-3 flex items-start gap-1.5 border-t border-border/60 pt-2 text-[10px] leading-relaxed text-muted-foreground">
          <Database className="mt-px h-3 w-3 shrink-0 opacity-60" />
          <span>
            Behind pace = completed points more than 10 percentage points below the share of the
            sprint window elapsed, assessed after the first quarter. Overran = the end date has
            passed while the sprint is still active. Bars measure current scope; the ± figure is the
            change since kickoff. One active sprint per board, across all {boards.length} boards.
            {unestimated > 0 &&
              ` · ${unestimated} sprint${unestimated === 1 ? '' : 's'} ${unestimated === 1 ? 'has' : 'have'} no point estimates and ${unestimated === 1 ? 'is' : 'are'} shown without a pace mark.`}
            {sprintsFailed > 0 &&
              ` · ${sprintsFailed} board${sprintsFailed === 1 ? '' : 's'} could not be checked.`}
          </span>
        </p>
      </section>
    </TooltipProvider>
  );
}

/** An emphasised fragment of the lede that scrolls to the rows proving it. */
function Anchor({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="font-semibold text-foreground underline-offset-[6px] decoration-transparent transition-colors hover:decoration-border focus-visible:decoration-border focus-visible:outline-none"
    >
      {children}
    </a>
  );
}

/** The rule, quotable. A judgement the reader cannot inspect is an assertion. */
function PaceRule({ boards }: { boards: number }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help border-b border-dotted border-muted-foreground/50">
          behind pace
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[42ch]">
        Behind pace: completed points are more than 10 percentage points below the share of the
        sprint window that has elapsed, assessed only after the first quarter of the sprint. A
        sprint whose end date has passed while it is still active also counts. Measured over the one
        active sprint on each of {boards} boards.
      </TooltipContent>
    </Tooltip>
  );
}

function Figure({ tip, children }: { tip: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help tabular-nums text-foreground/80 decoration-dotted underline-offset-2 transition-colors hover:text-foreground hover:underline">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

/** Never a partial sum. Counting, or unavailable — those are the only states. */
function Pending({ failed, total, label }: { failed: number; total: number; label: string }) {
  return (
    <span
      className="text-muted-foreground/40"
      title={
        failed > 0
          ? `Counts unavailable for ${failed} of ${total} boards.`
          : `Counting across ${total} boards…`
      }
    >
      ··· {label}
    </span>
  );
}

/**
 * One sprint, and whether it will land.
 *
 * The meter is the only repeated mark on the page, which is the whole reason it
 * can be read by comparison: the fill is the same brand colour at every value,
 * so nine rows differ only in length and in where the elapsed tick sits. A ramp
 * or a red-when-late fill would make each row a separate judgement to decode.
 */
function SprintRow({ board, sprint, pace }: Row) {
  const { donePct, elapsedPct, isAtRisk, isOverrun, scopeDelta } = pace;
  const stateWord = isOverrun ? 'Overran' : pace.isBehind ? 'Behind' : null;
  const daysLeft = sprint.endDate ? daysUntilDateOnly(sprint.endDate) : null;

  const ends = (() => {
    if (!sprint.endDate) return '—';
    if (daysLeft === null) return '—';
    if (daysLeft < 0) return `Ended ${formatDate(sprint.endDate, 'monthDay')}`;
    if (daysLeft === 0) return 'Ends today';
    if (daysLeft === 1) return 'Ends tomorrow';
    if (daysLeft < 7) return `Ends ${formatDate(sprint.endDate, { weekday: 'short' })}`;
    return `Ends ${formatDate(sprint.endDate, 'monthDay')}`;
  })();

  return (
    <Link
      to="/boards"
      search={{ board: board.id }}
      title={sprint.goal ?? undefined}
      className="group -mx-3 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 rounded-md px-3 py-2.5 transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring @2xl:grid-cols-[minmax(0,1fr)_12rem_6.5rem_5.5rem] @4xl:grid-cols-[minmax(0,1fr)_14rem_6.5rem_5.5rem]"
    >
      {/* Board first: in this workspace every sprint is called "Sprint 12", so
          the board is the only token that tells one row from another. */}
      <span className="flex min-w-0 items-baseline gap-2">
        <span
          className="h-2 w-2 shrink-0 self-center rounded-[2px]"
          style={{ background: board.color || 'hsl(var(--muted))' }}
        />
        <span className="truncate text-[13px] font-medium text-foreground">{board.name}</span>
        <Dot />
        <span className="truncate text-[11px] text-muted-foreground">{sprint.name}</span>
      </span>

      <span className="hidden @2xl:block">
        {donePct === null ? (
          <span className="text-[11px] text-muted-foreground">no point estimates</span>
        ) : (
          <span
            role="img"
            aria-label={`${Math.round(donePct)}% of points done${elapsedPct !== null ? `, ${Math.round(elapsedPct)}% of the sprint window elapsed` : ', sprint window unknown'}`}
            className="block w-full"
          >
            <span className="relative block">
              <span className="relative block h-1.5 overflow-hidden rounded-[1px] bg-muted">
                {/* The gap between done and elapsed becomes a shape rather than
                    an inference the reader has to make between two positions. */}
                {isAtRisk && elapsedPct !== null && elapsedPct > donePct && (
                  <span
                    aria-hidden
                    className="absolute inset-y-0"
                    style={{
                      left: `${donePct}%`,
                      width: `${elapsedPct - donePct}%`,
                      backgroundColor: 'var(--heat-loss)',
                      opacity: 0.25,
                    }}
                  />
                )}
                <span
                  className="absolute inset-y-0 left-0 rounded-r-[4px] bg-hygge transition-[width] duration-300 motion-reduce:transition-none"
                  style={{ width: `${donePct === 0 ? 0 : Math.max(donePct, 2)}%` }}
                />
              </span>
              {/* No tick at all when the window is unknown: a mark at zero would
                  be a fabricated position, which is worse than an absent one. */}
              {elapsedPct !== null && (
                <span
                  aria-hidden
                  className="absolute top-1/2 h-[10px] w-[2px] -translate-y-1/2 rounded-[1px] bg-foreground/50 ring-2 ring-background"
                  style={{ left: `calc(${elapsedPct}% - 1px)` }}
                />
              )}
            </span>
            <span className="mt-1 block text-[10px] tabular-nums text-muted-foreground">
              {stateWord && (
                <span className="font-medium text-destructive">{stateWord} · </span>
              )}
              {Math.round(donePct)}% done
              {elapsedPct !== null ? ` · ${Math.round(elapsedPct)}% elapsed` : ' · window unknown'}
            </span>
          </span>
        )}
      </span>

      <span
        className="hidden text-right text-[11px] tabular-nums text-muted-foreground @2xl:block"
        title={
          scopeDelta === null
            ? 'No start snapshot recorded for this sprint'
            : `${Math.abs(scopeDelta)} points ${scopeDelta > 0 ? 'added' : 'removed'} after kickoff (committed ${pace.committed})`
        }
      >
        {pace.completed}/{pace.current}
        {scopeDelta !== null && scopeDelta !== 0 && (
          <span className="ml-1 text-foreground/70">
            {scopeDelta > 0 ? '+' : '−'}
            {Math.abs(scopeDelta)}
          </span>
        )}
      </span>

      <span
        className={cn(
          'text-right text-[11px] tabular-nums',
          isOverrun ? 'font-medium text-destructive' : 'text-muted-foreground',
        )}
        title={sprint.endDate ? formatDate(sprint.endDate, 'long') : undefined}
      >
        {ends}
      </span>
    </Link>
  );
}
